import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import {
  ENGAGEMENT_PIPELINE_LABELS,
  STATUS_BADGE_CLASSES,
  type DashboardOutput,
  type DashboardFullOutput,
  type DashboardItemRow,
  type DashboardEngagementRow,
  type DashboardEngagementTaskRow,
  type DashboardBlockedRow,
  type DashboardDeadlineWindow,
  type DashboardMeetingRow,
  type DashboardWorkloadRow,
} from '@1financial/shared';
import { SkeletonBlock } from '../components/Skeleton.js';
import { ArrowRight, ArrowUp, ArrowDown, CalendarDays, SlidersHorizontal } from 'lucide-react';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

type DashboardLayoutPreference = { customised: boolean; order: string[]; hidden: string[] };
type SectionDefinition = { id: string; label: string; node: ReactNode };

export function DashboardPage() {
  const { user } = useAuth();
  const [customising, setCustomising] = useState(false);
  const [layout, setLayout] = useUserPreference<DashboardLayoutPreference>('dashboard.layout.v1', { customised: false, order: [], hidden: [] }, {
    validate: (value) => !!value && typeof value === 'object'
      && typeof (value as Record<string, unknown>).customised === 'boolean'
      && Array.isArray((value as Record<string, unknown>).order)
      && Array.isArray((value as Record<string, unknown>).hidden)
      && [...(value as Record<string, unknown>).order as unknown[], ...(value as Record<string, unknown>).hidden as unknown[]].every((item) => typeof item === 'string'),
  });

  const { data, isLoading, error } = useQuery<DashboardOutput>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await fetch('/v1/reports/dashboard', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: DashboardOutput };
      return body.data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 w-full min-w-0">
        <header>
          <h1 className="text-xl sm:text-2xl font-semibold text-white">Dashboard</h1>
          <p className="text-sm text-muted mt-1">Loading…</p>
        </header>
        <SkeletonBlock rows={3} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-bg-panel border border-border rounded-xl p-4 h-40 animate-pulse" />
          ))}
        </div>
        <SkeletonBlock rows={4} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full min-w-0">
        <h1 className="text-xl sm:text-2xl font-semibold text-white">Dashboard</h1>
        <p className="text-sm text-rose-300 mt-2">Failed to load dashboard: {error?.message ?? 'unknown error'}</p>
      </div>
    );
  }

  const firstName = user?.displayName?.split(' ')[0] ?? user?.email;
  const sections: SectionDefinition[] = [
    { id: 'myWork', label: 'My Work', node: <MyWorkSection myWork={data.myWork} /> },
    ...(data.todaysMeetings.length > 0 ? [{ id: 'meetings', label: "Today's meetings", node: <MeetingsSection meetings={data.todaysMeetings} /> }] : []),
    { id: 'attention', label: 'Needs attention', node: <DeadlineLadderSection overdue={data.deadlineLadder.overdue} today={data.deadlineLadder.today} unscheduled={data.deadlineLadder.unscheduled} /> },
    { id: 'upcoming', label: 'Upcoming deadlines', node: <UpcomingSection week={data.deadlineLadder.week} month={data.deadlineLadder.month} /> },
    ...(data.view === 'my-day'
      ? [{ id: 'myHours', label: 'My hours', node: <MyHoursSection today={data.myHours.today} week={data.myHours.week} /> }]
      : [
        { id: 'teamExceptions', label: 'Team exceptions', node: <TeamExceptionsSection rows={data.teamExceptions.rows} overdueBefore={data.deadlineLadder.overdue.dueBefore} /> },
        { id: 'teamPulse', label: 'Workload and team hours', node: <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6 [&>*]:min-w-0"><WorkloadSection workload={data.workload} /><TeamHoursSection teamHours={data.teamHours} /></div> },
        { id: 'pipeline', label: 'Pipeline', node: <PipelineSection pipeline={data.pipeline} /> },
      ]),
  ];
  const defaultOrder = sections.map((section) => section.id);
  const defaultHidden = user?.role === 'TeamLead' ? ['pipeline'] : [];
  const hidden = (layout.customised ? layout.hidden : defaultHidden).filter((id) => id !== 'myWork' && defaultOrder.includes(id));
  const savedOrder = layout.customised ? layout.order : [];
  const mergedOrder = [...new Set([...savedOrder, ...defaultOrder])].filter((id) => defaultOrder.includes(id));
  const orderedIds = ['myWork', ...mergedOrder.filter((id) => id !== 'myWork')];
  const sectionById = new Map(sections.map((section) => [section.id, section]));

  function toggleSection(id: string) {
    if (id === 'myWork') return;
    setLayout({ customised: true, order: orderedIds, hidden: hidden.includes(id) ? hidden.filter((item) => item !== id) : [...hidden, id] });
  }
  function moveSection(id: string, direction: -1 | 1) {
    const index = orderedIds.indexOf(id);
    const target = index + direction;
    if (index <= 0 || target <= 0 || target >= orderedIds.length) return;
    const next = [...orderedIds];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setLayout({ customised: true, order: next, hidden });
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white">Good {timeOfDay()}, {firstName}</h1>
          <p className="text-sm text-muted mt-1">Here's what needs attention today.</p>
        </div>
        <button type="button" onClick={() => setCustomising((value) => !value)} className="inline-flex items-center gap-2 text-xs text-muted hover:text-white border border-border hover:border-accent rounded-lg px-3 py-2">
          <SlidersHorizontal size={14} /> {customising ? 'Done' : 'Customise'}
        </button>
      </header>

      {customising && (
        <section className="bg-bg-panel border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted">Show, hide, or reorder sections. My Work stays first. Preferences are saved for you in this browser.</p>
            <button type="button" onClick={() => setLayout({ customised: false, order: [], hidden: [] })} className="text-xs text-accent hover:underline flex-shrink-0">Reset to role default</button>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {orderedIds.map((id, index) => (
              <li key={id} className="flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2 text-sm">
                <label className="flex items-center gap-2 text-white">
                  <input type="checkbox" checked={!hidden.includes(id)} disabled={id === 'myWork'} onChange={() => toggleSection(id)} />
                  {sectionById.get(id)?.label}
                </label>
                <span className="flex gap-1">
                  <button type="button" aria-label={`Move ${sectionById.get(id)?.label} up`} disabled={index <= 1} onClick={() => moveSection(id, -1)} className="text-muted hover:text-white disabled:opacity-30"><ArrowUp size={14} /></button>
                  <button type="button" aria-label={`Move ${sectionById.get(id)?.label} down`} disabled={index === orderedIds.length - 1} onClick={() => moveSection(id, 1)} className="text-muted hover:text-white disabled:opacity-30"><ArrowDown size={14} /></button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {orderedIds.filter((id) => !hidden.includes(id)).map((id) => <div key={id}>{sectionById.get(id)?.node}</div>)}
    </div>
  );
}

/* ─── Today's meetings ─────────────────────────────────────────────────────── */

function MeetingsSection({ meetings }: { meetings: DashboardMeetingRow[] }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white flex items-center gap-2">
          <CalendarDays size={15} className="text-muted" />
          Today's meetings <span className="text-xs text-muted font-normal">· {meetings.length}</span>
        </h2>
        <Link to="/meetings" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          Open calendar <ArrowRight size={12} />
        </Link>
      </div>
      <ul className="divide-y divide-border">
        {meetings.map((m) => (
          <li key={m.id} className="py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Link to="/meetings" className="text-sm text-white hover:text-accent block truncate">{m.title}</Link>
              <p className="text-xs text-muted truncate">
                {new Date(m.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {m.durationMin} min
                {m.clientName ? ` · ${m.clientName}` : ''}
                {m.location ? ` · ${m.location}` : ''}
              </p>
            </div>
            {m.meetingUrl && (
              <a
                href={m.meetingUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-accent hover:underline flex-shrink-0"
              >
                Join
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ─── ① My Work ──────────────────────────────────────────────────────────── */

function MyWorkSection({ myWork }: { myWork: DashboardOutput['myWork'] }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">
          My work <span className="text-xs text-muted font-normal">· {myWork.count} open{myWork.engagementTaskCount > 0 ? ` · ${myWork.engagementTaskCount} checklist tasks` : ''}</span>
        </h2>
        <Link to="/my-work" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          View all <ArrowRight size={12} />
        </Link>
      </div>
      {myWork.count === 0 ? (
        <p className="text-sm text-muted italic">Nothing assigned to you right now.</p>
      ) : (
        <div className="space-y-4">
          {myWork.engagements.length > 0 && (
            <div>
              <h3 className="text-xs text-muted uppercase tracking-wide mb-1">Engagements · {myWork.engagementCount}</h3>
              <ul className="divide-y divide-border">
                {myWork.engagements.map((engagement) => <EngagementRow key={engagement.id} engagement={engagement} />)}
              </ul>
            </div>
          )}
          {myWork.engagementTasks.length > 0 && (
            <div>
              <h3 className="text-xs text-muted uppercase tracking-wide mb-1">Engagement checklist · {myWork.engagementTaskCount}</h3>
              <ul className="divide-y divide-border">
                {myWork.engagementTasks.map((task) => <EngagementTaskRow key={task.id} task={task} />)}
              </ul>
            </div>
          )}
          {myWork.items.length > 0 && (
            <div>
              <h3 className="text-xs text-muted uppercase tracking-wide mb-1">Compliance · {myWork.complianceCount}</h3>
              <ul className="divide-y divide-border">
                {myWork.items.map((item) => <ItemRow key={item.id} item={item} overdue={isOverdue(item)} />)}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function EngagementTaskRow({ task }: { task: DashboardEngagementTaskRow }) {
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/engagements/${task.engagementId}`} className="text-sm text-white hover:text-accent block truncate">{task.title}</Link>
        <p className="text-xs text-muted truncate">{task.engagementTitle ?? 'Business setup'} · {task.clientName}</p>
      </div>
      <span className="px-2 py-0.5 rounded text-xs bg-bg-hover text-white flex-shrink-0">{task.status.replaceAll('_', ' ')}</span>
    </li>
  );
}

function EngagementRow({ engagement }: { engagement: DashboardEngagementRow }) {
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/engagements/${engagement.id}`} className="text-sm text-white hover:text-accent block truncate">
          {engagement.title ?? 'Business setup'}
        </Link>
        <p className="text-xs text-muted truncate">
          {engagement.clientName} {engagement.jurisdiction ? `· ${engagement.jurisdiction}` : ''}
        </p>
      </div>
      <div className="sm:text-right flex-shrink-0">
        <div className="text-xs text-muted">{engagement.endDate ? fmtDate(engagement.endDate) : 'No end date'}</div>
        <span className="px-2 py-0.5 rounded text-xs bg-bg-hover text-white">
          {ENGAGEMENT_PIPELINE_LABELS[engagement.pipeline] ?? engagement.pipeline}
        </span>
      </div>
    </li>
  );
}

/* ─── ② Deadline ladder ──────────────────────────────────────────────────── */

function DeadlineLadderSection({
  overdue,
  today,
  unscheduled,
}: {
  overdue: DashboardDeadlineWindow;
  today: DashboardDeadlineWindow;
  unscheduled: { count: number; items: DashboardItemRow[] };
}) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <LadderTile title="Overdue" sub="before today" window={overdue} accent="text-rose-400" />
      <LadderTile title="Due today" sub="today only" window={today} accent="text-amber-300" />
      <UnscheduledTile window={unscheduled} />
    </section>
  );
}

function LadderTile({
  title,
  sub,
  window: w,
  accent,
}: {
  title: string;
  sub: string;
  window: DashboardDeadlineWindow;
  accent: string;
}) {
  const params = new URLSearchParams();
  params.set('from', 'dashboard');
  params.set('open', 'true');
  if (w.dueAfter) params.set('dueDateAfter', w.dueAfter);
  params.set('dueDateBefore', w.dueBefore);
  params.set('sortBy', 'dueDate');
  const to = `/compliance?${params.toString()}`;
  return (
    <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col">
      <Link to={to} className="group block">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted">{title}</div>
          <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className={`text-xl sm:text-2xl font-semibold mt-1 ${accent}`}>{w.count}</div>
        <div className="text-xs text-muted mt-0.5">{sub}</div>
      </Link>
      {w.items.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-border space-y-2 flex-1">
          {w.items.map((item) => (
            <li key={item.id} className="flex items-baseline justify-between gap-2 text-xs">
              <Link to={`/compliance/${item.id}`} className="min-w-0 truncate text-white hover:text-accent">
                {item.serviceType}
                <span className="text-muted"> · {item.clientName}</span>
              </Link>
              <span className={`flex-shrink-0 ${isOverdue(item) ? 'text-rose-400 font-medium' : 'text-muted'}`}>
                {fmtDate(item.dueDate)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {w.count > w.items.length && (
        <Link to={to} className="mt-2 text-xs text-accent hover:underline">
          +{w.count - w.items.length} more
        </Link>
      )}
    </div>
  );
}

function UnscheduledTile({ window: w }: { window: { count: number; items: DashboardItemRow[] } }) {
  const to = '/compliance?from=dashboard&open=true&noDueDate=true';
  return (
    <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col">
      <Link to={to} className="group block">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted">No due date</div>
          <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="text-xl sm:text-2xl font-semibold mt-1 text-white">{w.count}</div>
        <div className="text-xs text-muted mt-0.5">open items needing a deadline</div>
      </Link>
      {w.items.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-border space-y-2 flex-1">
          {w.items.map((item) => (
            <li key={item.id} className="flex items-baseline justify-between gap-2 text-xs">
              <Link to={`/compliance/${item.id}`} className="min-w-0 truncate text-white hover:text-accent">
                {item.serviceType}<span className="text-muted"> · {item.clientName}</span>
              </Link>
              <span className="flex-shrink-0 text-muted">No date</span>
            </li>
          ))}
        </ul>
      )}
      {w.count > w.items.length && (
        <Link to={to} className="mt-2 text-xs text-accent hover:underline">+{w.count - w.items.length} more</Link>
      )}
    </div>
  );
}

/* ─── Upcoming deadlines ─────────────────────────────────────────────────── */

function UpcomingSection({ week, month }: { week: DashboardDeadlineWindow; month: DashboardDeadlineWindow }) {
  const [tab, setTab] = useUserPreference<'week' | 'month'>('dashboard.upcomingTab', 'week', {
    validate: (value) => value === 'week' || value === 'month',
  });
  const w = tab === 'week' ? week : month;
  const params = new URLSearchParams({ from: 'dashboard', open: 'true', sortBy: 'dueDate' });
  if (w.dueAfter) params.set('dueDateAfter', w.dueAfter);
  params.set('dueDateBefore', w.dueBefore);
  const to = `/compliance?${params.toString()}`;
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-medium text-white">Upcoming <span className="text-xs text-muted font-normal">· {w.count}</span></h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['week', 'month'] as const).map((value) => (
              <button key={value} onClick={() => setTab(value)} className={`px-3 py-1.5 text-xs ${tab === value ? 'bg-bg-hover text-white' : 'text-muted hover:text-white'}`}>
                {value === 'week' ? 'This week' : 'Later this month'}
              </button>
            ))}
          </div>
          <Link to={to} className="text-xs text-accent hover:underline inline-flex items-center gap-1">View all <ArrowRight size={12} /></Link>
        </div>
      </div>
      {w.items.length === 0 ? (
        <p className="text-sm text-muted italic">No deadlines in this window.</p>
      ) : (
        <ul className="divide-y divide-border">
          {w.items.map((item) => <ItemRow key={item.id} item={item} overdue={isOverdue(item)} />)}
        </ul>
      )}
      {w.count > w.items.length && <Link to={to} className="mt-2 inline-block text-xs text-accent hover:underline">+{w.count - w.items.length} more</Link>}
    </section>
  );
}

/* ─── Team exceptions ────────────────────────────────────────────────────── */

function TeamExceptionsSection({ rows, overdueBefore }: { rows: DashboardFullOutput['teamExceptions']['rows']; overdueBefore: string }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <h2 className="text-sm font-medium text-white mb-3">Team exceptions <span className="text-xs text-muted font-normal">· {rows.length}</span></h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted italic">No team exceptions today.</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li key={row.userId} className="py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <span className="text-sm text-white flex-shrink-0">{row.displayName}</span>
              <span className="flex flex-wrap items-center gap-1.5 text-xs">
                {row.overdueItems > 0 && <Link to={`/compliance?from=dashboard&assigneeId=${row.userId}&open=true&dueDateBefore=${encodeURIComponent(overdueBefore)}`} className="px-2 py-0.5 rounded pill-danger">{row.overdueItems} overdue</Link>}
                {row.blockedItems > 0 && <Link to={`/compliance?from=dashboard&assigneeId=${row.userId}&blocked=true`} className="px-2 py-0.5 rounded pill-warn">{row.blockedItems} blocked</Link>}
                {row.openItems >= 20 && <Link to={`/compliance?from=dashboard&assigneeId=${row.userId}&open=true`} className="px-2 py-0.5 rounded bg-bg-hover text-white">{row.openItems} open</Link>}
                {row.noTimeToday && <Link to="/time" className="px-2 py-0.5 rounded pill-warn">No time today</Link>}
                {row.missingCheckout && <Link to="/attendance" className="px-2 py-0.5 rounded pill-danger">No checkout</Link>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ─── ③ Workload by person ───────────────────────────────────────────────── */

function WorkloadSection({ workload }: { workload: DashboardFullOutput['workload'] }) {
  const groups: { label: string; rows: DashboardWorkloadRow[] }[] = [
    { label: 'Managers', rows: workload.managers },
    { label: 'Team Leads', rows: workload.teamLeads },
    { label: 'Staff', rows: workload.staff },
  ];
  const empty = groups.every((g) => g.rows.length === 0);
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <h2 className="text-sm font-medium text-white mb-3">Workload by person</h2>
      {empty ? (
        <p className="text-sm text-muted italic">No active team members.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {groups.map((g) =>
            g.rows.length === 0 ? null : (
              <div key={g.label}>
                <div className="text-xs text-muted uppercase tracking-wide mb-2">{g.label}</div>
                <ul className="space-y-1.5">
                  {g.rows.map((row) => (
                    <li key={row.userId}>
                      <Link
                        to={`/compliance?assigneeId=${row.userId}&open=true&sortBy=dueDate`}
                        className="group flex items-center justify-between gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-bg-hover/40"
                      >
                        <span className="text-white group-hover:text-accent truncate">{row.displayName}</span>
                        <span className="flex-shrink-0 text-xs space-x-2">
                          <span className="text-muted">{row.openItems} open</span>
                          {row.overdueItems > 0 && (
                            <span className="text-rose-400 font-medium">{row.overdueItems} overdue</span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ),
          )}
        </div>
      )}
    </section>
  );
}

/* ─── ④ Pipeline strip ───────────────────────────────────────────────────── */

function PipelineSection({ pipeline }: { pipeline: DashboardFullOutput['pipeline'] }) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Engagements in progress */}
      <div className="bg-bg-panel border border-border rounded-xl p-4">
        <Link to="/engagements" className="group block">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted">Engagements in progress</div>
            <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
          </div>
          <div className="text-xl sm:text-2xl font-semibold mt-1 text-white">{pipeline.engagements.inProgress}</div>
        </Link>
        <ul className="mt-3 pt-3 border-t border-border space-y-1.5">
          {pipeline.engagements.byStage.map((s) => (
            <li key={s.stage}>
              <Link
                to={`/engagements?pipeline=${s.stage}`}
                className="group flex items-center justify-between gap-2 text-xs rounded px-1.5 py-1 hover:bg-bg-hover/40"
              >
                <span className="text-muted group-hover:text-accent">
                  {ENGAGEMENT_PIPELINE_LABELS[s.stage] ?? s.stage}
                </span>
                <span className={s.count > 0 ? 'text-white font-medium' : 'text-muted'}>{s.count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Proposals — hidden server-side for roles without proposal visibility */}
      {pipeline.proposals && (
        <div className="bg-bg-panel border border-border rounded-xl p-4">
          <div className="text-xs text-muted">Proposals</div>
          <div className="flex items-end gap-6 mt-1">
            <Link to="/proposals?status=DRAFT" className="group">
              <div className="text-xl sm:text-2xl font-semibold text-white group-hover:text-accent">{pipeline.proposals.draft}</div>
              <div className="text-xs text-muted">Draft</div>
            </Link>
            <Link to="/proposals?status=SENT" className="group">
              <div className="text-xl sm:text-2xl font-semibold text-sky-300 group-hover:text-accent">{pipeline.proposals.sent}</div>
              <div className="text-xs text-muted">Sent</div>
            </Link>
          </div>
          {pipeline.proposals.top.length > 0 && (
            <ul className="mt-3 pt-3 border-t border-border space-y-2">
              {pipeline.proposals.top.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 text-xs">
                  <Link to={`/proposals/${p.id}`} className="min-w-0 truncate text-white hover:text-accent">
                    {p.subject}
                    <span className="text-muted"> · {p.clientName}</span>
                  </Link>
                  <span className="flex-shrink-0 px-2 py-0.5 rounded text-xs bg-bg-hover text-white">{p.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Blocked compliance work */}
      <div className="bg-bg-panel border border-border rounded-xl p-4">
        <Link to="/compliance?blocked=true" className="group block">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted">Blocked & stalled</div>
            <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
          </div>
          <div className={`text-xl sm:text-2xl font-semibold mt-1 ${pipeline.blocked.count > 0 ? 'text-rose-400' : 'text-white'}`}>
            {pipeline.blocked.count}
          </div>
          <div className="text-xs text-muted mt-0.5">waiting on client, sent back, or stale</div>
        </Link>
        {pipeline.blocked.top.length > 0 && (
          <ul className="mt-3 pt-3 border-t border-border space-y-2">
            {pipeline.blocked.top.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-2 text-xs">
                <Link to={`/compliance/${item.id}`} className="min-w-0 truncate text-white hover:text-accent">
                  {item.serviceType}
                  <span className="text-muted"> · {item.clientName}</span>
                </Link>
                <span className="flex-shrink-0 flex items-center gap-1.5">
                  <span className={reasonBadge(item.reason)}>{reasonLabel(item.reason)}</span>
                  <span className="text-muted">{item.daysStuck}d</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ─── ⑤ Team hours ───────────────────────────────────────────────────────── */

function TeamHoursSection({ teamHours }: { teamHours: DashboardFullOutput['teamHours'] }) {
  const maxToday = Math.max(8, ...teamHours.rows.map((r) => r.today));
  const maxWeek = Math.max(40, ...teamHours.rows.map((r) => r.week));
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">
          Team hours <span className="text-xs text-muted font-normal">· {formatHours(teamHours.totalToday)} today · {formatHours(teamHours.totalWeek)} this week</span>
        </h2>
        <Link to="/time" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          Time entries <ArrowRight size={12} />
        </Link>
      </div>
      {teamHours.rows.length === 0 ? (
        <p className="text-sm text-muted italic">No active team members.</p>
      ) : (
        <ul className="space-y-2">
          {teamHours.rows.map((row) => (
            <li key={row.userId} className="flex items-center gap-3 text-xs">
              <span className="w-32 flex-shrink-0 truncate text-white">
                {row.displayName}
                {row.today === 0 && (
                  <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" title="No time logged today" />
                )}
              </span>
              <span className="flex-1 flex items-center gap-2 min-w-0">
                <span className="h-2 rounded-full bg-bg-hover flex-1 overflow-hidden">
                  <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.min(100, (row.today / maxToday) * 100)}%` }} />
                </span>
                <span className="w-12 flex-shrink-0 text-right text-muted">{formatHours(row.today)}</span>
              </span>
              <span className="flex-1 flex items-center gap-2 min-w-0">
                <span className="h-2 rounded-full bg-bg-hover flex-1 overflow-hidden">
                  <span className="block h-full rounded-full bg-accent/60" style={{ width: `${Math.min(100, (row.week / maxWeek) * 100)}%` }} />
                </span>
                <span className="w-12 flex-shrink-0 text-right text-muted">{formatHours(row.week)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-3 mt-3 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-accent" /> today</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-accent/60" /> this week</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> no time logged today</span>
      </div>
    </section>
  );
}

/* ─── My-day extras (Staff) ──────────────────────────────────────────────── */

function MyHoursSection({ today, week }: { today: number; week: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">My hours</h2>
        <Link to="/time" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          Log time <ArrowRight size={12} />
        </Link>
      </div>
      <div className="flex items-end gap-8">
        <div>
          <div className="text-xl sm:text-2xl font-semibold text-emerald-300">{formatHours(today)}</div>
          <div className="text-xs text-muted mt-0.5">today</div>
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-semibold text-white">{formatHours(week)}</div>
          <div className="text-xs text-muted mt-0.5">this week</div>
        </div>
      </div>
    </section>
  );
}

/* ─── Shared bits ────────────────────────────────────────────────────────── */

function ItemRow({ item, overdue }: { item: DashboardItemRow; overdue?: boolean }) {
  const assignees = [
    item.assignee1Name ? `A1 ${item.assignee1Name}` : null,
    item.assignee2Name ? `A2 ${item.assignee2Name}` : null,
    item.managerName ? `mgr ${item.managerName}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/compliance/${item.id}`} className="text-sm text-white hover:text-accent block truncate">
          {item.serviceType}
        </Link>
        <p className="text-xs text-muted truncate">
          {item.clientName} {item.jurisdiction ? `· ${item.jurisdiction}` : ''}
        </p>
        {assignees && <p className="text-xs text-muted truncate">{assignees}</p>}
      </div>
      <div className="sm:text-right flex-shrink-0">
        <div className={`text-xs ${overdue ? 'text-rose-400 font-medium' : 'text-white'}`}>
          {item.dueDate ? fmtDate(item.dueDate) : 'No date'}
        </div>
        <span className={statusBadge(item.status)}>{item.status}</span>
      </div>
    </li>
  );
}

function isOverdue(item: DashboardItemRow): boolean {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return item.dueDate != null && new Date(item.dueDate).getTime() < today.getTime();
}

function timeOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function statusBadge(status: string): string {
  const classes = STATUS_BADGE_CLASSES[status as keyof typeof STATUS_BADGE_CLASSES];
  return classes ?? 'px-2 py-0.5 rounded text-xs bg-bg-hover text-white';
}

const REASON_BADGE_CLASSES: Record<DashboardBlockedRow['reason'], string> = {
  WaitingOnClient: 'px-2 py-0.5 rounded text-xs border pill-warn',
  SentBack: 'px-2 py-0.5 rounded text-xs border pill-danger',
  Stale: 'px-2 py-0.5 rounded text-xs border pill-neutral',
};

function reasonBadge(reason: DashboardBlockedRow['reason']): string {
  return REASON_BADGE_CLASSES[reason];
}

function reasonLabel(reason: DashboardBlockedRow['reason']): string {
  if (reason === 'WaitingOnClient') return 'Waiting on client';
  if (reason === 'SentBack') return 'Sent back';
  return 'Stale';
}

function formatHours(hours: number): string {
  return `${Number(hours.toFixed(1))}h`;
}

